import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Space, Switch, Typography, message } from "antd";
import { ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import Utilities from "../../dependencies/custom/react-utilities";
import Settings from "../../dependencies/custom/settings";

export default function ReportColumnSettings() {
  const [savedValue, setSavedValue] = useState(null);
  const [showBot, setShowBot] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const url = Settings.urls.bootstrap.reportColumnSettings;

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setSavedValue(null);
    setError("");
    try {
      const response = await Utilities.fetchWithAuth("GET", url);
      if (!response?.ok || typeof response.data?.show_bot !== "boolean") {
        setError(response?.msg || "Unable to load report column settings.");
        return;
      }
      setSavedValue(response.data.show_bot);
      setShowBot(response.data.show_bot);
    } catch {
      setError("Unable to load report column settings.");
    } finally {
      setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveSettings = async () => {
    setSaving(true);
    setError("");
    try {
      const response = await Utilities.fetchWithAuth("PATCH", url, {
        show_bot: showBot,
      });
      if (!response?.ok) {
        setError(
          response?.data?.show_bot?.[0] ||
            response?.data?.detail ||
            response?.data?.error ||
            response?.msg ||
            "Unable to save report column settings.",
        );
        return;
      }
      const value =
        typeof response.data?.show_bot === "boolean"
          ? response.data.show_bot
          : showBot;
      setSavedValue(value);
      setShowBot(value);
      message.success("Report column settings updated.");
    } catch {
      setError("Unable to save report column settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Report columns" className="mb-4">
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Typography.Text type="secondary">
          Choose whether the BOT column appears on academic reports.
        </Typography.Text>
        {error && <Alert type="error" showIcon message={error} />}
        <Space>
          <Switch
            id="report-show-bot"
            checked={showBot}
            onChange={setShowBot}
            loading={loading}
            disabled={loading || saving || savedValue === null}
          />
          <label htmlFor="report-show-bot">Show BOT</label>
        </Space>
        <Space wrap>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={saveSettings}
            loading={saving}
            disabled={loading || savedValue === null || showBot === savedValue}
          >
            Save changes
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadSettings}
            loading={loading}
            disabled={saving}
          >
            Reload
          </Button>
        </Space>
      </Space>
    </Card>
  );
}
